import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import getConfigByEnvironment from 'config/environmentConfig';
import getFeatureFlagsByEnvironment, { FeatureFlagsNames } from 'config/featureFlags';
import { envData } from 'environments';
import { handleOverrideFeatureFlags } from 'server/utility/utils';
import { setCommonState } from 'store/slices/common';
import { createAppStore } from 'store/store';
import {
  StoreIdentifierName,
  CsvData,
  CommunicationSection,
  FooterLink,
  ReduxState,
  HeaderFunctions,
  QuickviewService,
  SkuFavoriteService,
} from 'types';
import HeaderFooter from 'pages/header-footer/HeaderFooter';

import './injectNavigation.scss';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';

declare global {
  // eslint-disable-next-line vars-on-top, no-var
  var navigationService: {
    injectNavigation: (config: Config) => void;
  };

  export interface Window {
    headerData: {
      storeIdentifierName: string;
      csvData: CsvData[];
      communicationSection?: CommunicationSection;
      footerLinks?: FooterLink[];
      searchApi?: string;
      thumbnailApi?: string;
      tickerApi?: string;
    };
    headerReduxData: ReduxState;
    cartCountComponent?: {
      setCartCount: (value: string | number) => void;
    };
    headerFunctions: HeaderFunctions;
    quickview: QuickviewService;
    skuFavorite: SkuFavoriteService;
    navigationService: {
      injectNavigation: (config: Config) => void;
    };
  }
}

type FeatureFlagOverrides = Partial<{
  [flagKey in FeatureFlagsNames]: Partial<{
    [brandKey in StoreIdentifierName]: boolean;
  }>;
}>;

interface Config {
  element: HTMLElement;
  route?: 'header' | 'footer';
  storeIdentifierName?: string;
  featureFlagOverrides?: FeatureFlagOverrides;
  withFonts?: boolean;
}

const injectNavigation = (config: Config) => {
  // get overrides from config
  const {
    element,
    route = 'header',
    storeIdentifierName = 'dsg',
    featureFlagOverrides = {},
    withFonts = true,
  } = config;

  const featureFlagEnv: string =
    process.env.LOCAL_DEV === 'true' ? 'perf' : process.env.profile ?? 'perf';
  const envFeatureFlags = getFeatureFlagsByEnvironment(featureFlagEnv);
  const overridenFlags = handleOverrideFeatureFlags(envFeatureFlags, featureFlagOverrides);
  const environmentConfig = getConfigByEnvironment(featureFlagEnv);

  // get remaining fields needed for common state
  const partyKey = process.env.HEADER_PARTY_KEY;
  const productDetailsHost = process.env.CATALOG_PRODUCTDETAILS_API_HOST;
  const storeIdentifierNameNoAOS = storeIdentifierName.replace('aos', '') as StoreIdentifierName;

  let apiObject = {};
  if (route === 'header') {
    apiObject = {
      searchApi: `${process.env.SEARCH_API}?storeId=${
        envData[storeIdentifierNameNoAOS as keyof typeof envData].storeId
      }&catalogId=${
        envData[storeIdentifierNameNoAOS as keyof typeof envData].catalogId
      }&searchTerm=`,
      tickerApi: process.env.TICKER_API,
      thumbnailApi: process.env.THUMBNAIL_API,
      storeByNumberEndpoint: process.env.STORE_BY_NUMBER_ENDPOINT,
      storesByLocationEndpoint: process.env.STORES_BY_LOCATION_ENDPOINT,
      productDetailsEndpoint: productDetailsHost,
    };
  } else {
    apiObject = {
      smsSignUpUrl: process.env.FOOTER_DSG_SMS_SIGNUP_URL ?? '',
      smsSignUpApiKey: process.env.FOOTER_DSG_SMS_APIM_KEYVALUE ?? '',
    };
  }

  // get scoped css classes
  const homefieldTheme = storeIdentifierNameNoAOS === 'plf' ? 'pl' : storeIdentifierNameNoAOS;
  const scopeClasses = [`calia-react-${route}-scope`, `hmf-${homefieldTheme}-theme`];

  // get theme fonts and add them to the document
  if (withFonts) {
    let fonts: string[] = [];
    if (homefieldTheme in envData) {
      fonts = envData[homefieldTheme as keyof typeof envData].fonts;
    }
    fonts.forEach((fontUrl) => {
      const fontLink = document.createElement('link');
      fontLink.setAttribute('href', fontUrl);
      fontLink.setAttribute('type', 'text/css');
      fontLink.setAttribute('rel', 'stylesheet');
      document.head.appendChild(fontLink);
    });
  }

  // Set up redux store
  const commonState = {
    partyKey,
    aos: storeIdentifierName.includes('aos'),
    storeIdentifierName: storeIdentifierNameNoAOS,
    brandConfig: envData[storeIdentifierNameNoAOS as keyof typeof envData],
    headerFooterType: route === 'header' ? 'header' : 'footer',
    apiEndpoints: apiObject,
    featureFlags: overridenFlags,
    environmentConfig,
    serverHttpHeaders: {},
    headersToForward: {},
  };
  const store = createAppStore();
  store.dispatch(setCommonState(commonState));

  element.classList.add(...scopeClasses);

  // render the application
  createRoot(element).render(
    <StrictMode>
      <Provider store={store}>
        <ErrorBoundaryWithLogger componentId="CSR-HeaderFooter">
          <HeaderFooter route={route} store={store} />
        </ErrorBoundaryWithLogger>
      </Provider>
    </StrictMode>,
  );
};

window.navigationService = {
  injectNavigation,
};

globalThis.navigationService = {
  injectNavigation,
};

export default injectNavigation;
